import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Ruler, User, ChevronRight } from 'lucide-react';
import oronasalMeasurementImg from '@/assets/oronasal-measurement.jpg';

interface MaskSizingProps {
  maskType: 'Nasal' | 'Oronasal';
  onComplete: (result: {
    maskSize: 'S' | 'M' | 'L';
    measurementMm: number;
    measureMethod: 'regla' | 'manual';
    borderline: boolean;
  }) => void;
  onBack: () => void;
  onStepChange?: (stepInfo: {
    step: 'method' | 'measure' | 'manual';
    measureMethod: 'regla' | 'manual' | null;
    previousStep: 'method' | 'measure' | 'manual' | null;
  }) => void;
}

export default function MaskSizing({ maskType, onComplete, onBack, onStepChange }: MaskSizingProps) {
  const [step, setStep] = useState<'method' | 'measure' | 'manual'>('method');
  const [previousStep, setPreviousStep] = useState<'method' | 'measure' | 'manual' | null>(null);
  const [measureMethod, setMeasureMethod] = useState<'regla' | 'manual'>('regla');
  const [measurementMm, setMeasurementMm] = useState<number>(0);
  const [maskSize, setMaskSize] = useState<'S' | 'M' | 'L'>('M');
  const [borderline, setBorderline] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);

  // Notify parent of initial step state and scroll to top when step changes
  useEffect(() => {
    onStepChange?.({
      step,
      measureMethod,
      previousStep
    });

    // Scroll to top when step changes (for internal navigation)
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    });
  }, [step, measureMethod, previousStep, onStepChange]);

  // Listen for step change events from parent (header navigation)
  useEffect(() => {
    const handleStepChangeEvent = (event: CustomEvent) => {
      const { action } = event.detail;
      
      if (action === 'goToMeasure') {
        setStep('measure');
      } else if (action === 'goToManual') {
        setStep('manual');
      } else if (action === 'goToMethod') {
        setStep('method');
        setPreviousStep(null);
      }
    };

    window.addEventListener('maskSizingStepChange', handleStepChangeEvent as EventListener);
    return () => window.removeEventListener('maskSizingStepChange', handleStepChangeEvent as EventListener);
  }, []);

  // Reset image loaded state when step changes to measure
  useEffect(() => {
    if (step === 'measure') {
      setImageLoaded(false);
    }
  }, [step, maskType]);


  const handleMethodSelection = (method: 'regla' | 'manual') => {
    setMeasureMethod(method);
    setPreviousStep('method');
    
    const newStep = method === 'regla' ? 'measure' : 'manual';
    setStep(newStep);
    
    onStepChange?.({
      step: newStep,
      measureMethod: method,
      previousStep: 'method'
    });
  };

  const handleMeasurement = () => {
    if (measurementMm > 0) {
      // Calculate the mask size directly without setting state
      let calculatedSize: 'S' | 'M' | 'L' = 'M';
      let isBorderline = false;
      let alert = '';

      if (maskType === 'Nasal') {
        // N20 calculations
        isBorderline = measurementMm >= 30 && measurementMm <= 37;
        
        // Only show alert for measurements below minimum range
        if (measurementMm < 23) {
          alert = 'La medida parece fuera de rango. Te ayudamos por WhatsApp para confirmar.';
        }
        
        if (measurementMm < 30) calculatedSize = 'S';
        else if (measurementMm >= 30 && measurementMm < 45) calculatedSize = 'M';
        else calculatedSize = 'L'; // If >= 45, it's Large (no upper limit)
        
        // If borderline between S and M, choose M
        if (isBorderline && calculatedSize === 'S') calculatedSize = 'M';
      } else {
        // F20 calculations
        isBorderline = Math.abs(measurementMm - 86) <= 2 || Math.abs(measurementMm - 98) <= 2;
        
        // Only show alert for measurements below minimum range
        if (measurementMm < 74) {
          alert = 'La medida parece fuera de rango. Te ayudamos por WhatsApp para confirmar.';
        }
        
        if (measurementMm >= 74 && measurementMm < 86) calculatedSize = 'S';
        else if (measurementMm >= 86 && measurementMm < 98) calculatedSize = 'M';
        else calculatedSize = 'L'; // If >= 98, it's Large (no upper limit)
        
        // If borderline between sizes, choose the larger one
        if (isBorderline) {
          if (Math.abs(measurementMm - 86) <= 2 && calculatedSize === 'S') calculatedSize = 'M';
          if (Math.abs(measurementMm - 98) <= 2 && calculatedSize === 'M') calculatedSize = 'L';
        }
      }

      // Complete the flow directly with the calculated values
      onComplete({
        maskSize: calculatedSize,
        measurementMm,
        measureMethod,
        borderline: isBorderline
      });
    }
  };

  const handleManualSelection = (size: 'S' | 'M' | 'L') => {
    // Complete the flow directly with the manually selected size
    onComplete({
      maskSize: size,
      measurementMm: 0, // No measurement for manual selection
      measureMethod,
      borderline: false
    });
  };



  const getSizeAdvice = (size: 'S' | 'M' | 'L') => {
    if (maskType === 'Nasal') {
      switch (size) {
        case 'S': return 'Ideal si tienes nariz pequeña o delgada';
        case 'M': return 'Talla más común, para nariz promedio';
        case 'L': return 'Ideal si tienes nariz ancha o grande';
      }
    } else {
      switch (size) {
        case 'S': return 'Ideal si tienes cara pequeña o delgada';
        case 'M': return 'Talla más común, para cara promedio';
        case 'L': return 'Ideal si tienes cara ancha o grande';
      }
    }
  };

  // Method Selection Screen
  if (step === 'method') {
    return (
      <div className="min-h-screen bg-soft-bg">
        <div className="container px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-primary mb-2">
                Encontremos la talla ideal para tu mascarilla {maskType.toLowerCase()}
              </h1>
            </div>

            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="space-y-4">
                  <button
                    onClick={() => handleMethodSelection('regla')}
                    className="w-full p-4 text-left border rounded-lg transition-colors hover:bg-primary/5 focus:outline-none"
                  >
                    <div className="flex items-center space-x-3 relative">
                      <Ruler className="h-6 w-6 min-w-6 text-primary" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-primary">Medir con regla</h3>
                      </div>
                      <Badge variant="default" className="absolute -top-7 -right-2 bg-accent">
                        Recomendado
                      </Badge>
                    </div>
                  </button>

                  <button
                    onClick={() => handleMethodSelection('manual')}
                    className="w-full p-4 text-left border rounded-lg transition-colors hover:bg-primary/5 focus:outline-none"
                  >
                    <div className="flex items-center space-x-3">
                      <User className="h-6 w-6 min-w-6 text-primary" />
                      <div>
                        <h3 className="font-semibold">Elegir talla manualmente</h3>
                      </div>
                    </div>
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Measurement Screen
  if (step === 'measure') {
    const measurementImage = maskType === 'Nasal' ? 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/Medida_Nasal.png' : 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/Medida_Oronasal.png';
    const measurementInstructions = maskType === 'Nasal' 
      ? [
          'Usa una regla o cinta métrica',
          'Colócala horizontalmente sobre tu nariz',
          'Mide el ancho en la parte más ancha de las fosas nasales',
          'Anota la medida en milímetros'
        ]
      : [
          'Usa una regla o cinta métrica',
          'Colócala verticalmente en tu rostro',
          'Mide desde el puente nasal hasta la hendidura del mentón',
          'Anota la medida en milímetros'
        ];

    return (
      <div className="min-h-screen bg-soft-bg">
        <div className="container px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-4">
              <h1 className="text-xl font-bold text-primary mb-2">
                {maskType === 'Nasal' 
                  ? 'Mide el ancho de tu nariz'
                  : 'Mide desde tu puente nasal hasta la hendidura de tu mentón'
                }
              </h1>
              <h2 className='text-xl font-semibold'>en milímetros</h2>
            </div>

            {/* Measurement Image and Instructions */}
            <Card className="mb-6">
              <CardContent className="space-y-4 pt-6">
                <div className="w-full relative" style={{ aspectRatio: maskType === 'Nasal' ? '10/10' : '10/10' }}>
                  {!imageLoaded && (
                    <div className="absolute inset-0 bg-gray-100 rounded-lg border animate-pulse flex items-center justify-center">
                      <span className="text-gray-400">Cargando imagen...</span>
                    </div>
                  )}
                  <img 
                    src={measurementImage} 
                    alt={`Instrucciones de medición para mascarilla tipo ${maskType}`}
                    className={`w-full h-full rounded-lg border object-contain ${!imageLoaded ? 'invisible' : ''}`}
                    onLoad={() => setImageLoaded(true)}
                  />
                </div>
                
                <div className="p-4 rounded-lg">
                  <h4 className="text-xl font-medium mb-4">Instrucciones:</h4>
                  <ol className="space-y-4 text-sm">
                    {measurementInstructions.map((instruction, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-5 h-5 bg-primary text-primary-foreground rounded-full text-xs flex items-center justify-center mt-0.5">
                          {index + 1}
                        </span>
                        <span className="text-md">{instruction}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </CardContent>
            </Card>

            {/* Input Section */}
            <Card>
              <CardContent className="p-6 space-y-6">
                <div className="space-y-4">
                  <CardTitle className="flex items-center gap-2">
                    {maskType === 'Nasal' ? 'Ingresa el ancho de tu nariz (mm)' : 'Ingresa la distancia entre tu puente nasal y tu mentón (mm)'}
                  </CardTitle>
                  <Input
                    id="measurement"
                    type="number"
                    placeholder={maskType === 'Nasal' ? 'Ej: 35' : 'Ej: 85'}
                    value={measurementMm || ''}
                    onChange={(e) => setMeasurementMm(Number(e.target.value))}
                    min={0}
                    max={maskType === 'Nasal' ? 100 : 140}
                    className="text-xl text-center"
                  />
                  <p className="text-s text-muted-foreground text-center">
                    Rango típico: {maskType === 'Nasal' ? '23-55 mm' : '74-110 mm'}
                  </p>
                </div>

                {showAlert && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{alertMessage}</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            <div className="mt-6">
              <Button className="min-h-touch px-6 sm:px-8 gap-0 w-full text-lg" onClick={handleMeasurement} disabled={measurementMm <= 0}>
                <span className="">Calcular Talla</span>
                <ChevronRight style={{ marginLeft: '2.5px', width: '1.25rem', height: '1.25rem', strokeWidth: '3px'}}/>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Manual Selection Screen
  if (step === 'manual') {
    return (
      <div className="min-h-screen bg-soft-bg">
        <div className="container px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-4">
              <h1 className="text-3xl font-bold text-primary mb-2">
                Elige tu talla
              </h1>
            </div>

            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="space-y-4">
                  {Object.entries({'S': 'Pequeña', 'M': 'Mediana', 'L': 'Grande'}).map(([size, text_value]) => (
                    <button
                      key={size}
                      onClick={() => handleManualSelection(size as 'S' | 'M' | 'L')}
                      className="w-full p-4 text-left border rounded-lg transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">{text_value}</h3>
                          <p className="text-sm text-muted-foreground">
                            {getSizeAdvice(size as 'S' | 'M' | 'L')}
                          </p>
                        </div>
                        {/* <Badge variant={size === 'M' ? 'default' : 'secondary'}>
                          {size === 'M' ? 'Más común' : size}
                        </Badge> */}
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    );
  }


  return null;
}